import React from 'react';
import { SalesOrderInfo } from '../../../../store/slices/salesOrders';
import classes from './TableRow.module.css';
import { format, parseISO } from 'date-fns';
import { ReportTableColumn, RowAction } from './interfaces';
import { ICustomer } from '../../../../store/slices/customers';
import { ISalesOrderProduct } from '../../../../store/slices/salesOrderProducts';
import moment from 'moment';
import { statusNameById } from '../../../../utils/statusUtils';
import { IDigitalHealthProfile } from '../../SelectedSalesOrder/DigitalHealthProfileTab';
import { getDateLocale } from '../../../../utils/dateLocale';
import PrimaryButtonV2 from '../../Buttons/PrimaryButtonV2';
import { useAppSelector } from '../../../../store/store';
import { IAccountInfo, selectAccountInfo } from '../../../../store/slices/auth';
import { IOtherService } from '../../../../screens/VideoConsultingRoom/VideoMenu/AdditionalServicesTab';
import { useTranslation } from '../../../../hooks/useTranslations';

const getStatusClass = (status_id: number, missedAppointment: boolean) => {

  if (missedAppointment) {
    return classes.statusMissed;
  }
  if (status_id === 2) {
    return classes.statusCompleted;
  } else if (status_id === 1) {
    return classes.statusPending;
  } else if (status_id === 4) {
    return classes.statusParked;
  } else if (status_id === 5) {
    return classes.statusPrepared;
  } else if (status_id === 6) {
    return classes.statusStarted;
  } else if (status_id === 3) {
    return classes.statusVoid;
  }
  return classes.statusOpened;
};

// The following function is used to extract row data from column path
// Ex: Let's assume we have the following object const foo = { bar: { someKey: 'some value'} }
// In order to extract the value of someKey we send the following path: 'bar.someKey'
const getCellDataByPath = (
  obj: SalesOrderInfo | ICustomer | ISalesOrderProduct | IDigitalHealthProfile | IOtherService,
  column: ReportTableColumn,
  translationFunction: (key: string) => string,
  isMobileClinic?: boolean,
  isConsultation?: boolean,
  missedAppointment?: boolean

) => {
  if (!obj) {
    return '';
  }
  const pathList = column.path.split('.');
  let result: any = obj;
  for (let i = 0; i < pathList.length; i++) {
    if (!result) {
      result = null;
      break;
    }
    result = result[pathList[i]];
  }
  // Based on the column type we return the desired value for the cell
  if (column.type === 'DATETIME' && result) {
    return format(parseISO(result), 'dd.MM.yyyy HH:mm');
  }
  if (column.type === 'GENDER' && result) {
    return translationFunction(`${result.toLowerCase()}`);
  }
  if (column.type === 'APPOINTMENT') {
    return result
      ? format(parseISO(result), 'dd.MM.yyyy HH:mm')
      : translationFunction('label.walk_in');
  }
  if (column.type === 'DATE' && result) {
    const lang = localStorage.getItem('lang') || 'en';
    return format(parseISO(result), 'dd.MM.yyyy', {
      locale: getDateLocale(lang),
    });
  }
  if (column.type === 'STATUS') {
    return (
      <div className={getStatusClass(result, Boolean(missedAppointment))}>
        {statusNameById(translationFunction, result, isMobileClinic, isConsultation, missedAppointment)}
      </div>
    );
  }
  if (column.type === 'DATENUMBERED' && result) {
    return moment(result).format('DD.MM.YYYY');
  }
  if (column.type === 'TIME' && result) {
    return moment(result).format('HH:mm');
  }
  if (column.type === 'SUBTOTAL_AMOUNT' && obj) {
    if ('amount_paid' in obj && 'vat' in obj)
      return Number(Number(obj.amount_paid) * (1 - Number(obj.vat) / 100)).toFixed(2);
  }
  return result;
};

const TableRow: React.FC<{
  row: SalesOrderInfo | ICustomer | ISalesOrderProduct | IDigitalHealthProfile | IOtherService;
  actions: RowAction[];
  columns: ReportTableColumn[];
  lastRow: boolean;
  isInCheckIn?: boolean;
  isInReport?: boolean;
}> = ({ row, actions, columns, lastRow, isInCheckIn, isInReport }) => {
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const salesOrderRow = row as SalesOrderInfo;
  const hideEditInReport =
    isInReport && ![2, 4].includes(salesOrderRow.work_order?.work_order_status_id ?? -1);
  let bgColor: string = '#fff';
  if (
    isInCheckIn &&
    salesOrderRow.diagnostic_results &&
    salesOrderRow.diagnostic_results.length > 0
  ) {
    if (salesOrderRow.diagnostic_results[0].biomarker_value_result) {
      bgColor = '#e5f2fc';
    }
  }

  const translations = useTranslation();

  return (
    <tr
      className={classes.reportTableRow}
      style={{
        backgroundColor: bgColor,
      }}
    >
      {columns.map((column, index) => {
        return (
          <td
            key={index}
            className={`${classes[`reportTableColumn${column.type}`]} ${lastRow ? '' : classes.borderBottom
              }`}
          >
            {getCellDataByPath(
              row,
              column,
              translations.translations,
              accountInfo.consultation_with_extra_steps,
              !!salesOrderRow.work_order_service_consultation?.work_order_id
              // Boolean(
              //   (row as SalesOrderInfo)?.sales_order_appointment?.sales_order_appointment_status ===
              //     'M'
              // )
            )}
          </td>
        );
      })}
      {actions.length > 0 && (
        <td className={`${classes.reportTableColumn} ${lastRow ? '' : classes.borderBottom}`}>
          <div className={classes.reportTableActionsColumn}>
            {!hideEditInReport &&
              actions.map((action, index) => (
                <div key={index} className={classes.reportTableActionsWithRenderer}>
                  {action.renderer ? (
                    <div onClick={() => action.onClick(row)}>{action.renderer}</div>
                  ) : action.displayPrimaryButton ? (
                    <PrimaryButtonV2
                      onClick={() => action.onClick(row)}
                      buttonStyle={{
                        height: 28,
                        borderRadius: 4,
                        fontSize: 14,
                        padding: '0 10px',
                      }}
                      text={action.displayName ?? ''}
                    />
                  ) : (
                    <div
                      className={
                        isInCheckIn ? classes.checkInTableAction : classes.tableActionButton
                      }
                      onClick={() => action.onClick(row)}
                    >
                      {isInReport ? (
                        salesOrderRow.work_order?.work_order_status_id === 2 ? (
                          <span>{translations.translations('view')}</span>
                        ) : (
                          <span>{translations.translations('edit')}</span>
                        )
                      ) : (
                        action.displayName
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </td>
      )}
    </tr>
  );
};

export { TableRow };
